// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Metric from './Metric';
import LineChart from './LineChart';
import api from '../../api';

import formatCurrency from '../../utilities/formatCurrency';

import '../../styles/investments.css';
import '../../styles/investments-summary.css';
import '../../styles/investments-filters.css';
import '../../styles/paginator.css';
import Listable from './Listable';
import Paginator from '../Paginator';
import ColoredTag from '../ColoredTag';
import TitleLink from '../TitleLink';
import Link from './Link';
import Filter from './Filter';
import DoughnutChart  from './DoughnutChart';


export default class Summary extends React.Component {
    abortController = new AbortController()

    state = {
        fmv: undefined,
        numPlans: undefined,
        numAccounts: undefined,
        cashflow: undefined,
        assets: undefined,
        stats: undefined,
        resultLimit: 20, 
        plans: [],
        loading: false, 
        currentPage: 0,
        pageSize: 20,
        totalPlanCount: 0,
    }
    
    componentDidMount = async () => {
        // Set the title of the main page... 
        this.props.setTitle("Investments and Trust Accounting")
        this.loadStats();
        this.loadPlans(); 
    }

    componentDidUpdate = (prev) => {
        if (JSON.stringify(prev.filters) !== JSON.stringify(this.props.filters) || this.props.query !== prev.query) {
            this.setState({plans: []}, () => this.loadPlans());
        }
        if (prev.planYear !== this.props.planYear) {
            this.setState({
                plans: [],
                fmv: undefined,
                numPlans: undefined,
                numAccounts: undefined,
                cashflow: undefined,
                assets: undefined,
                stats: undefined,
            }, () => {
                this.loadPlans()
                this.loadStats();
            });
        }
    }

    loadStats = async () => {
        this.setState({ loading: true }); 
        const fmv = async() => {
            const res = await api.get('/tpa/statistics/fmv'); 
            this.setState({ fmv: res.success ? res.fmv : '' });
        }
        const counts = async() => {
            const res = await api.get('/tpa/statistics/counts');
            this.setState({ 
                numPlans: res.success ? res.plans?.active : '',
                numAccounts: res.success ? res.accounts : '',
            });
        }
        const cashflow = async() => {
            const res = await api.get(`/tpa/statistics/cashflow?planYear=${this.props.planYear}`);
            this.setState({ cashflow: res.success ? res.cashflow : [] });
        }
        const assets = async() => {
            const res = await api.get(`/tpa/statistics/assets?planYear=${this.props.planYear}`);
            this.setState({ assets: res.success ? res.assets : [] });
        }
        const status = async() => {
            const res = await api.post(`/tpa/status/accounts`, { planYear: this.props.planYear });
            this.setState({ stats: res.success ? res.stats : '' });
        }

        await Promise.all([fmv(), counts(), cashflow(), assets(), status()]);
        this.setState({loading: false}); 
    }

    loadPlans = async () => {

        // Abort the last request for loading in plans
        this.abortController.abort()
        this.abortController = new AbortController()

        this.setState({ loading: true });

        // Create url params for the request... 
        let params = new URLSearchParams(); 
        params.append("resultLimit", this.state.pageSize);
        params.append('resultOffset', this.state.currentPage * this.state.pageSize); 
        params.append('includeAssets', true);
        params.append('planYear', this.props.planYear); 
        if(!this.props.filters?.planStatus?.some(s => s === 'inactive'))
            params.append('active', true);


        // Any filters to apply? 
        const {planName, internalPlanId, planType, planStatus, assignees} = {...this.props.filters};
        if (planName?.length) params.append('planName', planName);
        if (internalPlanId?.length) params.append('internalPlanId', internalPlanId);
        if (planType?.length) params.append('planType', planType);
        if (planStatus?.length) params.append('planStatus', planStatus);
        if (assignees?.length) params.append('assignees', assignees);   
        

        
        const url = '/tpa/plan/list?' + params.toString() + `&search=${encodeURIComponent(this.props.query)}`; 
        const res = await api.get(url, {signal: this.abortController.signal}, false, {}, true);  

        // TODO: This is not the best way to handle this... We have to reload pages we aleady have been on. However this is simple, and solves the issule of jumping between multiple pages, or quickly going through them. 
        let plans = Array(res.totalPlanLength).fill({}); 
        // Replace the section of the plans array that represents the current page...
        for(let i = this.state.currentPage * this.state.pageSize; i < this.state.pageSize * (this.state.currentPage + 1); i++){
            plans[i] = res?.plans?.[i - this.state.currentPage * this.state.pageSize] || {};
        }

        // remove any empty entries...
        plans = plans.filter(p => p._id);

        this.setState({
            plans: plans,
            totalPlanCount: res.totalPlanLength, 
            loading: false 
        });
    }   

    renderStats = () => {
        return (
            <>
                <Metric 
                    label="Fair Market Value (FMV)" 
                    value={typeof this.state.fmv === 'number' ? formatCurrency(this.state.fmv, 1) : undefined}
                /> 
                <Metric 
                    label="Active Plans" 
                    value={typeof this.state.numPlans === 'number' ? this.state.numPlans.toLocaleString('en-US') : undefined}
                /> 
                <Metric 
                    label="Investment Accounts" 
                    value={typeof this.state.numAccounts === 'number' ? this.state.numAccounts.toLocaleString('en-US') : undefined}
                />
                <div style={{ clear: 'both', height: 20 }} />
                <LineChart
                    user={this.props.user}
                    title="Investment Assets"
                    data={this.state.assets}
                    styles={{width: 'calc(45% - 40px)'}}
                    labels={[{
                        label: 'Total Balance',
                        key: 'balance',
                    }]}
                    yMin={0}
                />
                <LineChart
                    user={this.props.user}
                    title="Cash Flow summary"
                    data={this.state.cashflow}
                    styles={{width: 'calc(45% - 40px)'}}
                    labels={[{
                        label: 'Deposits',
                        key: 'deposits',
                    }, {
                        label: 'Withdrawals',
                        key: 'withdrawals',
                    }, {
                        label: 'Transfers',
                        key: 'transfers',
                    }, {
                        label: 'Net',
                        key: 'net'
                    }]}
                />
                <div style={{ clear: 'both', 'height': 10}}/>
                <div
                    style={{paddingLeft: 20}}
                >
                    <div
                        style={{display: 'flex', gap: 30, 'alignItems': 'center', marginBottom: 20}}
                    >
                        <h3 style={{fontWeight: 600}}>Trust Accounting Status</h3>
                        <TitleLink label="See status report →" link={`/investments/status?planYear=${this.props.planYear}`}/> 
                    </div>
                    <DoughnutChart 
                         styles={{
                            width: 'calc(35% - 40px)',
                            height: 'auto', 
                            float: 'left',
                            padding: 0,
                        }}
                        colors={[
                            '#00b770',
                            '#24f1be',
                            'rgb(255, 129, 59)',
                            '#ff4352',
                        ]}
                        labels={[
                            "Complete Accounts", 
                            "On Track Accounts", 
                            "Accounts To Review", 
                            "Accounts with Missing Statements"
                        ]}
                        data={this.state.stats?[
                            this.state.stats?.numAccountsByStatus?.complete,
                            this.state.stats?.numAccountsByStatus?.onTrack || 0,
                            this.state.stats?.numAccountsByStatus?.todo || 0,
                            this.state.stats?.numAccountsByStatus?.missing || 0
                        ]: undefined}
                    /> 
                    <Metric 
                     label="Statements missing" 
                     value={
                        ((this.state.stats?.totalMissingStatements) || (this.state.stats? 0: undefined ))?.toLocaleString('en-US')
                     }
                    /> 
                    <Metric 
                     label={
                        <>Accounts on track<Link href='investments/status?show=on-track'/></>
                     }
                     value={
                        ((this.state.stats?.numAccountsByStatus?.onTrack + this.state.stats?.numAccountsByStatus?.complete)
                        || (this.state.stats? 0: undefined))?.toLocaleString('en-US')
                    }
                    /> 
                </div>
                
            </>
        )
    }

    render = () => {
        return (
            <>
                <div style={{ clear: 'both', height: 20}}/>
                {this.renderStats()}
                <div style={{ clear: 'both', height: 50 }} />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        paddingLeft: 20,
                    }}
                >
                    <h3 style={{fontWeight: 600}}>My Retirement Plans</h3>
                    <div style={{marginLeft: 30}}/>
                    <TitleLink
                        link={'/settings/plan-editor'}
                        label={'Edit plans and accounts →'}
                    />
                    <div style={{marginLeft: 'auto'}}/>
                    { !this.state.loading ?
                        <Paginator 
                            items={[...this.state.plans]}
                            currentPage={this.state.currentPage} 
                            pageSize={this.state.pageSize}
                            setPage={(n) => {
                                this.setState(prevState => ({currentPage: prevState.currentPage + n}), () => {
                                    this.loadPlans(); 
                                })
                            }}
                            setLastPage={(page) => {
                                this.setState({currentPage: page}, () => {
                                    this.loadPlans(); 
                                })
                            }}
                            setPageLimit={limit => this.setState({pageSize: limit, currentPage: 0}, () => { this.loadPlans()})}  
                            totalLength={this.state.totalPlanCount}
                        /> 
                        : ""
                    }

                    <Filter 
                        onClick={() => {
                            // this.props.modal('filter-plans', {accounts: this.state.accounts, filterAccounts: this.filterAccounts})
                            this.props.modal('filter-plans', {plans: this.state.plans})
                        }}
                        label="Filter Plans"
                        filterCount={Object.values(this.props.filters).filter((f) => f !== null && f?.length > 0).length} 
                        onClear={async() => {
                            await this.props.clearFilters();
                            // Reload the accounts... 
                            this.loadPlans();
                        }}
                    /> 
                </div>
                <div style={{ clear: 'both', height: 35 }} />
                <Listable 
                    loading={this.state.loading}
                    pageSize={this.state.pageSize}
                    page={this.state.currentPage}
                    styles={{paddingLeft: 20}}
                    dynamicHeight={true}
                    stickyHeader={true}
                    columns={[
                        {
                            header: "Plan Id",
                            prop: "internalPlanId",
                            width: 80, 
                            align: 'left',
                            cursor: 'pointer',
                            render: (item) => {
                                return item.internalPlanId;
                            }
                        },
                        {
                            header: "Plan Name",
                            prop: "planName",
                            width: 300, 
                            align: 'left',
                            cursor: 'pointer',
                            render: (item) => {
                                return item.planName;
                            }
                        },
                        {
                            header: "Plan Type",
                            prop: "planType",
                            width: 110, 
                            align: 'left',
                            cursor: 'pointer',
                            render: (item) => {
                                return item.planType;
                            }
                        },
                        {
                            header: "Status",
                            prop: "status",
                            sortComparitor: (item => item.status?.[this.props.planYear] || 'missing'),
                            width: 110, 
                            align: 'left',
                            cursor: 'pointer',
                            render: (item) => {
                                const status = item?.status?.[this.props.planYear]??'missing';

                                // Define the colors for each possible status... (complete, onTrack, missing, todo, na)
                                const colors = {
                                    na: {
                                        backgroundColor: 'var(--palette-color-6)',
                                        color: 'white',
                                    },

                                    missing: {
                                        backgroundColor: 'var(--palette-color-1)',
                                        color: 'white',
                                    },

                                    todo: {
                                        backgroundColor: 'var(--color-accent)',
                                        color: 'white',
                                    },

                                    onTrack: {
                                        backgroundColor: 'var(--palette-color-8)',
                                        color: 'white',
                                    },

                                    complete: {
                                        backgroundColor: 'var(--palette-color-3)',
                                        color: 'white',
                                    },
                                    inactive: {
                                        backgroundColor: 'var(--palette-color-7)',
                                        color: 'white',
                                    }
                                }

                                return status ? <ColoredTag {...colors[status]} label={status === 'onTrack' ? 'On Track' : status}/> : "";
                            }
                        },
                        {
                            header: "Assets",
                            prop: "totalAssets",
                            width: 149, 
                            align: 'right',
                            cursor: 'pointer',
                            render: (item) => {
                                if(!item.totalAssets)
                                    return '-'
                                const amount = parseFloat(item.totalAssets ?? 0);
                                const formattedAmount = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                return amount < 0 ? `($${formattedAmount.replace('-', '')})` : `$${formattedAmount}`;
                            }
                        },
                        {
                            header: "Deposits",
                            prop: "totalDeposits",
                            width: 149, 
                            align: 'right',
                            cursor: 'pointer',
                            render: (item) => {
                                if(!item.totalDeposits)
                                    return '-'
                                const amount = parseFloat(item.totalDeposits ?? 0);
                                const formattedAmount = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                return amount < 0 ? `($${formattedAmount.replace('-', '')})` : `$${formattedAmount}`;
                            }
                        },
                        {
                            header: "Withdrawals",
                            prop: "totalWithdrawals",
                            width: 149, 
                            align: 'right',
                            cursor: 'pointer',
                            render: (item) => {
                                if(!item.totalWithdrawals)
                                    return '-'
                                const amount = parseFloat(item.totalWithdrawals ?? 0);
                                const formattedAmount = amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                return amount < 0 ? `($${formattedAmount.replace('-', '')})` : `$${formattedAmount}`;
                            }
                        },
                    ]}
                    data={this.state.plans}
                    onRowClick={(item) => {
                        window.location.href = `/investments/plan/${item._id}?planYear=${this.props.planYear}`;
                        // this.props.history.push(`/investments/plan/${item._id}`);
                        this.props.setPlan({...item});
                    }}
                />
                <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 30,
                    marginBottom: 40
                }}
                >
                    <Paginator 
                        items={[...this.state.plans]}
                        currentPage={this.state.currentPage} 
                        pageSize={this.state.pageSize}
                        setPage={(n) => {
                            this.setState(prevState => ({currentPage: prevState.currentPage + n}), () => {
                                this.loadPlans(); 
                            })
                        }}
                        setLastPage={(page) => {
                            this.setState({currentPage: page}, () => {
                                this.loadPlans(); 
                            })
                        }}
                        setPageLimit={limit => this.setState({pageSize: limit, currentPage: 0}, () => { this.loadPlans()})} 
                        totalLength={this.state.totalPlanCount}
                        bottom={true}
                    /> 
                </div>


            </>
        )
    }
}